/**
 * context/app/useProjectActions.js - project create / update / save / delete
 * (the editor's saves hand back { ok, status, body }).
 * Moved verbatim out of AppProvider (AppContext.jsx), which calls it at the
 * same point (same hook order) with the state it reads.
 */
import { useCallback } from 'react';
import { toSnake, changesTouchAccounts } from './rows';
import { API_BASE, sendProject } from './apiClient';

export function useProjectActions({ settleProject, dropProject, refreshQuietly }) {
  // -- PROJECTS ------------------------------------------------
  // Returns the created row. The caller needs its id: files picked in the Doc
  // Hub while the opportunity was still being typed are held in the browser
  // and uploaded once there is a record to hang them on.
  const addProject = useCallback(
    async (d) => {
      try {
        const res = await fetch(`${API_BASE}/api/projects`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(toSnake(d)),
        });
        const created = res.ok ? await res.json().catch(() => null) : null;
        settleProject(created);
        return created;
      } catch (err) {
        console.error('addProject error:', err);
        return null;
      }
    },
    [settleProject]
  );

  // Returns true/false. It used to return nothing, so a caller could not tell
  // a failed save from a successful one - the project health flag has to,
  // because reporting "saved" on a colour that did not save is worse than
  // showing an error.
  const updateProject = useCallback(
    async (id, updates) => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(toSnake(updates)),
        });
        if (res.ok) {
          settleProject(await res.json().catch(() => null), { accounts: changesTouchAccounts(updates) });
          return true;
        }
        if (res.status === 404) {
          const payload = { ...updates, id };
          const made = await fetch(`${API_BASE}/api/projects`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(toSnake(payload)),
          });
          settleProject(made.ok ? await made.json().catch(() => null) : null);
          return made.ok;
        }
        return false;
      } catch (err) {
        console.error('updateProject error:', err);
        return false;
      }
    },
    [settleProject]
  );

  // The two saves the opportunity editor and the Projects form use. They hand
  // back the server's whole answer, { ok, status, body }, because a 409 is a
  // question for the person saving, not a failure to hide: 'quote_taken'
  // (someone just used that quote number - routes/projects.js names the next
  // free one) and 'edit_conflict' (someone changed a field you are changing
  // since you opened it). No 404 -> POST fallback in saveProject: the editor
  // only edits rows it loaded, so a miss must never become an insert.
  const createProject = useCallback(
    async (data, { allowDuplicateQuote } = {}) => {
      try {
        const payload = toSnake(data || {});
        // one quote can carry two lines (a Paint and a Sealer) - said on purpose
        if (allowDuplicateQuote) payload.allow_duplicate_quote = true;
        const out = await sendProject('/api/projects', 'POST', payload);
        if (out.ok) settleProject(out.body);
        return out;
      } catch (err) {
        console.error('createProject error:', err);
        return { ok: false, status: 0, body: null };
      }
    },
    [settleProject]
  );

  // `changes` = only the fields the user edited; `expect` = what those fields
  // held when the editor opened. The server refuses (edit_conflict) if any of
  // them has changed since, unless `force` ("Keep mine").
  // A good save answers as soon as the server has: its row goes into the
  // lists, and they re-read behind it (settleProject). The editor rebases
  // its next save on that answer's values (rawValue(res.body)), as before.
  const saveProject = useCallback(
    async (id, changes, { expect, force } = {}) => {
      try {
        const payload = toSnake(changes || {});
        if (expect) payload._expect = toSnake(expect);
        if (force) payload._force = true;
        const out = await sendProject(`/api/projects/${encodeURIComponent(id)}`, 'PUT', payload);
        if (out.ok) settleProject(out.body, { accounts: changesTouchAccounts(changes) });
        return out;
      } catch (err) {
        console.error('saveProject error:', err);
        return { ok: false, status: 0, body: null };
      }
    },
    [settleProject]
  );

  // Returns true/false, like updateProject: the server can refuse a delete
  // (the area gate's 403, a 404 for a row already gone), and a refusal must
  // not read as a success.
  const deleteProject = useCallback(
    async (id) => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/${id}`, { method: 'DELETE' });
        if (!res.ok) return false;
        dropProject(id);
        refreshQuietly(['projects', 'accounts']);
        return true;
      } catch (err) {
        console.error('deleteProject error:', err);
        return false;
      }
    },
    [dropProject, refreshQuietly]
  );

  // assignEngineerToProject / unassignEngineerFromProject are gone with the
  // routes behind them. They wrote BOTH sides - projects.engineer and
  // engineers.project - so a change on the Projects tab edited a row on the
  // Engineers tab. Scheduling lives in the Engineers tab's calendar.
  return { addProject, updateProject, createProject, saveProject, deleteProject };
}
